/**
 * Централизованный модуль для расчёта цен
 * Единый источник истины для всех расчётов цен в приложении
 */

export const CAKE_WEIGHT = 357;

export interface PriceCalculationInput {
  pricePerGram: number;
  weight: number;
  quantity?: number;
  cakePriceAmount?: number | null;
  cakeWeight?: number | null;
}

export interface PriceCalculationResult {
  unitPrice: number;    // Цена за единицу (за блин или за грамм)
  subtotal: number;     // Итоговая сумма
  isCake: boolean;      // Это блин?
  usedFormula: boolean; // Использовалась ли формула?
}

/**
 * Рассчитывает цену за блин по формуле (только для стандартного веса 357г)
 * k = 0.89 - 0.0032 * pricePerGram
 * price = pricePerGram * 357 * k (округляется до 100)
 */
export function calculateCakePrice(pricePerGram: number): number {
  const k = 0.89 - 0.0032 * pricePerGram;
  const price = pricePerGram * CAKE_WEIGHT * k;
  return Math.round(price / 100) * 100;
}

/**
 * Получить цену за блин (серверная или рассчитанная)
 */
export function getCakePrice(pricePerGram: number, cakePriceAmount?: number | null): number {
  if (cakePriceAmount && cakePriceAmount > 0) {
    return cakePriceAmount;
  }
  return calculateCakePrice(pricePerGram);
}

/**
 * Получить допустимые варианты веса для чая
 */
export function getValidWeights(
  isCakeAvailable: boolean,
  isCakeOnly: boolean,
  cakeWeight?: number | null,
): number[] {
  const effectiveCakeWeight = cakeWeight ?? CAKE_WEIGHT;
  if (isCakeOnly) return [effectiveCakeWeight];
  if (isCakeAvailable) return [10, 50, 100, effectiveCakeWeight];
  return [10, 50, 100];
}

/**
 * Рассчитать цену для чая (основная функция)
 */
export function calculateTeaPrice(input: PriceCalculationInput): PriceCalculationResult {
  const { pricePerGram, weight, quantity = 1, cakePriceAmount, cakeWeight } = input;
  const effectiveCakeWeight = cakeWeight ?? CAKE_WEIGHT;
  const isCake = weight === effectiveCakeWeight;

  if (isCake) {
    let unitPrice: number;
    let usedFormula = false;

    if (effectiveCakeWeight !== CAKE_WEIGHT) {
      // Нестандартный вес блина — формула не применяется, только ручная цена
      unitPrice = (cakePriceAmount && cakePriceAmount > 0)
        ? cakePriceAmount
        : pricePerGram * effectiveCakeWeight;
    } else {
      // Стандартный вес 357г — формула или ручная цена
      unitPrice = getCakePrice(pricePerGram, cakePriceAmount);
      usedFormula = !(cakePriceAmount && cakePriceAmount > 0);
    }

    return {
      unitPrice,
      subtotal: unitPrice * quantity,
      isCake: true,
      usedFormula,
    };
  }

  // Обычный вес (10г, 50г, 100г)
  const unitPrice = pricePerGram * weight;
  return {
    unitPrice,
    subtotal: unitPrice * quantity,
    isCake: false,
    usedFormula: false,
  };
}

/**
 * Рассчитать итоговую сумму для массива товаров
 */
export function calculateTotal<T extends {
  productType: string;
  weight: number | null;
  quantity: number;
  product?: {
    priceAmount: number;
    cakePriceAmount?: number | null;
    cakeWeight?: number | null;
  } | null;
}>(items: T[]): number {
  return items.reduce((sum, item) => {
    if (item.productType !== 'tea' || !item.weight) {
      // Для посуды или без веса — просто price * quantity
      const price = item.product?.priceAmount ?? 0;
      return sum + price * item.quantity;
    }

    const result = calculateTeaPrice({
      pricePerGram: item.product?.priceAmount ?? 0,
      weight: item.weight,
      quantity: item.quantity,
      cakePriceAmount: item.product?.cakePriceAmount,
      cakeWeight: item.product?.cakeWeight,
    });

    return sum + result.subtotal;
  }, 0);
}
